import type { IEmployeeApiPort } from '@personnel/application/ports/IEmployeeApiPort';
import type { EmployeeDto, EmployeeCreateDto } from '@personnel/application/dtos/EmployeeResponseDto';
import { errorAttributes, perLog } from '@personnel/application/observability';

/**
 * Use Case for creating a new employee
 *
 * The write also sends the invitation the person needs to activate their account, which is why its
 * failure rate is watched: nobody finds out on their own that the mail never went out.
 *
 * @param {Object} props - Use case dependencies
 * @param {IEmployeeApiPort} props.apiPort - Port for API operations
 * @returns {Object} Use case executor with execute method
 */
export const createEmployeeUseCase = (props: { apiPort: IEmployeeApiPort }) => {
  const { apiPort } = props;

  /**
   * Executes the employee creation
   *
   * @param {EmployeeCreateDto} request - Employee creation data
   * @returns {Promise<EmployeeDto>} Created employee data
   */
  const execute = async (request: EmployeeCreateDto): Promise<EmployeeDto> => {
    const attributes = {
      'per.roleUuid': request.roleUuid,
      'per.hasPin': request.posPin.trim().length > 0,
      'per.storeCount': request.assignedStores.length,
      'per.allStores': request.allStoresAssigned,
    };

    try {
      const created = await apiPort.createEmployee(request);

      perLog.info('employeeCreated', attributes);

      return created;
    } catch (error) {
      perLog.error('employeeCreated', { ...attributes, ...errorAttributes(error) });
      throw error;
    }
  };

  return { execute };
};
